import { useState, useEffect } from 'react';
import { auth, db, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  verifyPasswordResetCode,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { AppUser } from '../types';

function mapUserDoc(
  uid: string,
  email: string,
  data: Record<string, unknown> | undefined
): AppUser {
  if (!data) {
    return { uid, email, role: 'viewer' };
  }
  return { uid, email: (data.email as string) || email, ...data } as AppUser;
}

export function useAuth() {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        setUser(
          mapUserDoc(
            firebaseUser.uid,
            firebaseUser.email || '',
            userDoc.exists() ? (userDoc.data() as Record<string, unknown>) : undefined
          )
        );
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const refreshUser = async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      setUser(null);
      return;
    }
    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    setUser(
      mapUserDoc(
        firebaseUser.uid,
        firebaseUser.email || '',
        userDoc.exists() ? (userDoc.data() as Record<string, unknown>) : undefined
      )
    );
  };

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  /** "Forgot password": the sendPasswordReset Cloud Function emails a link to /reset (plan 16). */
  const resetPassword = async (email: string, language: string) => {
    await httpsCallable<{ email: string; lang: string }, { ok: boolean }>(functions, 'sendPasswordReset')({
      email: email.trim(),
      lang: language,
    });
  };

  /** /reset page: which email the link is for (throws when the link is used or expired). */
  const checkResetCode = (code: string) => verifyPasswordResetCode(auth, code);

  /** /reset page: saves the new password. */
  const finishReset = (code: string, newPassword: string) => confirmPasswordReset(auth, code, newPassword);

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  /**
   * Makes sure the signed-in user has a /users profile as an owner ('editor'),
   * so they can open My Items. Existing profiles are left alone.
   */
  const ensureOwnerProfile = async (displayName?: string) => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return;
    const existing = await getDoc(doc(db, 'users', firebaseUser.uid));
    if (!existing.exists()) {
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        email: (firebaseUser.email || '').toLowerCase(),
        role: 'editor',
        ...(displayName?.trim() ? { displayName: displayName.trim() } : {}),
      });
    }
    await refreshUser();
  };

  /** New account for someone adding a listing (docs/directory-plan.md 10.5). */
  const signUp = async (email: string, password: string, displayName?: string) => {
    await createUserWithEmailAndPassword(auth, email.trim(), password);
    await ensureOwnerProfile(displayName);
  };

  return { user, loading, signIn, signOut, signUp, ensureOwnerProfile, refreshUser, resetPassword, checkResetCode, finishReset };
}
